/**
 * Server state for the waiter surface. The backend is the only source of truth: every cart line and every
 * session on screen comes from the last read, and after every action the data is read again. Realtime events
 * are refetch signals and never carry state.
 *
 * Recovery, in order: refetch on `stream.ready` (every connect and reconnect), on `stream.resync`, when the
 * tab becomes visible again, and after every action. While the stream is not live a slow poll keeps the
 * screen honest; while it is live there is no polling at all.
 *
 * Nothing is retried by the client. Adding to the customer's cart has one Idempotency-Key and one expected
 * revision per intent (`useAddIntent`): pressing Add again with the same inputs after an uncertain failure
 * sends the same key and the same revision, so the backend replays the first result instead of adding a
 * second line (it checks the key before the revision). A conflict or an answer ends the intent.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useStaffSession } from '@/auth/react';
import { useToast } from '@/components/Toast';
import { newIdempotencyKey } from '@/lib/ids';
import { useRealtimeEvent, useRealtimeState } from '@/realtime/react';
import { staffFloorApi } from './api';
import type { AddToCartInput, OpenSessionInput, TransferInput } from './api';

/** Poll interval while the live stream is down. Not used while it is live. */
export const FALLBACK_POLL_MS = 30_000;
/** Events that arrive together become one refetch. */
export const COALESCE_MS = 100;

type Family = 'floor' | 'sessions' | 'session' | 'menu' | 'assistance' | 'ready';
const ALL: Family[] = ['floor', 'sessions', 'session', 'menu', 'assistance', 'ready'];

export const staffKeys = {
  family: (branchId: string | null, family: Family) => ['staff', branchId, family] as const,
  session: (branchId: string | null, sessionId: string | null) =>
    ['staff', branchId, 'session', sessionId] as const,
};

function useBranchId(): string | null {
  return useStaffSession()?.user.branchId ?? null;
}

function useLiveRefetch() {
  return useRealtimeState() === 'live' ? false : FALLBACK_POLL_MS;
}

export function useFloor() {
  const branchId = useBranchId();
  const refetchInterval = useLiveRefetch();
  return useQuery({
    queryKey: staffKeys.family(branchId, 'floor'),
    queryFn: ({ signal }) => staffFloorApi.getFloor(signal),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval,
  });
}

/** Carries the cart summary and the kinds of help asked for. A second read beside the floor. */
export function useActiveSessions() {
  const branchId = useBranchId();
  const refetchInterval = useLiveRefetch();
  return useQuery({
    queryKey: staffKeys.family(branchId, 'sessions'),
    queryFn: ({ signal }) => staffFloorApi.getActiveSessions(signal),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval,
  });
}

export function useSessionDetail(sessionId: string | null) {
  const branchId = useBranchId();
  const refetchInterval = useLiveRefetch();
  return useQuery({
    queryKey: staffKeys.session(branchId, sessionId),
    queryFn: ({ signal }) => staffFloorApi.getSession(sessionId as string, signal),
    enabled: sessionId !== null,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval,
  });
}

export function useMenu(enabled = true) {
  const branchId = useBranchId();
  const refetchInterval = useLiveRefetch();
  return useQuery({
    queryKey: staffKeys.family(branchId, 'menu'),
    queryFn: ({ signal }) => staffFloorApi.getMenu(signal),
    enabled,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval,
  });
}

/** Orders the kitchen has finished, waiting to be served. */
export function useReadyOrders() {
  const branchId = useBranchId();
  const refetchInterval = useLiveRefetch();
  return useQuery({
    queryKey: staffKeys.family(branchId, 'ready'),
    queryFn: ({ signal }) => staffFloorApi.getReadyOrders(signal),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval,
  });
}

export function useAssistance() {
  const branchId = useBranchId();
  const refetchInterval = useLiveRefetch();
  return useQuery({
    queryKey: staffKeys.family(branchId, 'assistance'),
    queryFn: ({ signal }) => staffFloorApi.getAssistance(signal),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval,
  });
}

/**
 * Maps staff stream events to refetches while a waiter screen is open. Duplicate and bursty events are
 * harmless: they only mark queries for refetch, and marks made within `COALESCE_MS` run once. Only events the
 * waiter's audience includes are subscribed. The backend sends a waiter no `bill.updated` or `payment.updated`
 * (they carry amounts), and nothing on a waiter screen depends on them.
 */
export function useStaffLive(enabled = true) {
  const queryClient = useQueryClient();
  const branchId = useBranchId();
  const pending = useRef<Set<Family>>(new Set());
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(
    () => () => {
      if (timer.current !== undefined) clearTimeout(timer.current);
      timer.current = undefined;
    },
    [],
  );

  const mark = (...families: Family[]) => {
    if (!enabled) return;
    for (const family of families) pending.current.add(family);
    if (timer.current !== undefined) return;
    timer.current = setTimeout(() => {
      timer.current = undefined;
      const due = [...pending.current];
      pending.current = new Set();
      for (const family of due) {
        void queryClient.invalidateQueries({ queryKey: staffKeys.family(branchId, family) });
      }
    }, COALESCE_MS);
  };

  const sessionsChanged = () => mark('floor', 'sessions', 'session');
  const ordersChanged = () => mark('floor', 'sessions', 'session', 'ready');
  const helpChanged = () => mark('floor', 'sessions', 'session', 'assistance', 'ready');
  const everything = () => mark(...ALL);

  useRealtimeEvent('cart.updated', sessionsChanged);
  useRealtimeEvent('order.created', sessionsChanged);
  // A cancelled or voided line can leave the rest of an order READY, so these refresh the ready list too.
  useRealtimeEvent('order.cancelled', ordersChanged);
  useRealtimeEvent('order.item_voided', ordersChanged);
  useRealtimeEvent('order.item_status_changed', () => mark('floor', 'session', 'ready'));
  useRealtimeEvent('assistance.requested', helpChanged);
  useRealtimeEvent('assistance.resolved', helpChanged);
  useRealtimeEvent('session.opened', sessionsChanged);
  useRealtimeEvent('session.transferred', helpChanged);
  useRealtimeEvent('session.closed', helpChanged);
  useRealtimeEvent('menu.availability_changed', () => mark('menu'));
  useRealtimeEvent('stream.resync', everything);
  useRealtimeEvent('stream.ready', everything);
}

/** What an action may have changed is read again; after a success the action stays pending until it is. */
function refreshStaff(
  queryClient: ReturnType<typeof useQueryClient>,
  branchId: string | null,
  families: Family[],
  failed = false,
) {
  const reads = families.map((family) =>
    queryClient.invalidateQueries({ queryKey: staffKeys.family(branchId, family) }),
  );
  return failed ? undefined : Promise.all(reads);
}

/**
 * One Idempotency-Key and one expected revision per "add to the customer's cart" the waiter means. While the
 * inputs stay the same the key and the revision first used are reused, so a repeat after a lost answer is a
 * replay, not a second line. Changing an input starts a new intent; `reset()` ends it once the backend has
 * answered (success or refusal).
 */
export function useAddIntent() {
  const current = useRef<{ signature: string; key: string; revision: number } | null>(null);
  const intentFor = useCallback((signature: string, revision: number) => {
    if (current.current?.signature !== signature) {
      current.current = { signature, key: newIdempotencyKey(), revision };
    }
    return { key: current.current.key, revision: current.current.revision };
  }, []);
  const reset = useCallback(() => {
    current.current = null;
  }, []);
  return { intentFor, reset };
}

/** One Idempotency-Key per user intent: the same key while the inputs are the same, a new one when they change. */
export function useIntentKey() {
  const current = useRef<{ signature: string; key: string } | null>(null);
  const keyFor = useCallback((signature: string) => {
    if (current.current?.signature !== signature) {
      current.current = { signature, key: newIdempotencyKey() };
    }
    return current.current.key;
  }, []);
  const reset = useCallback(() => {
    current.current = null;
  }, []);
  return { keyFor, reset };
}

/** Errors stay with the sheet that shows them; success is announced there too. */
export function useAddToCart() {
  const queryClient = useQueryClient();
  const branchId = useBranchId();
  return useMutation({
    mutationFn: (vars: { sessionId: string; input: AddToCartInput; idempotencyKey: string }) =>
      staffFloorApi.addToCart(vars.sessionId, vars.input, vars.idempotencyKey),
    onSettled: (_data, error) =>
      refreshStaff(queryClient, branchId, ['session', 'sessions', 'floor', 'menu'], error !== null),
  });
}

export function useResolveAssistance() {
  const queryClient = useQueryClient();
  const branchId = useBranchId();
  const toast = useToast();
  return useMutation({
    mutationFn: (requestId: string) => staffFloorApi.resolveAssistance(requestId),
    onSuccess: () => toast.show('Request resolved.', { tone: 'success', durationMs: 3000 }),
    onSettled: (_data, error) =>
      refreshStaff(
        queryClient,
        branchId,
        ['assistance', 'floor', 'sessions', 'session'],
        error !== null,
      ),
  });
}

/**
 * Serves one READY order. Sent once and never retried; the error stays with the order that was pressed.
 * After any answer the ready list and the floor are read again, so the screen shows the server's state.
 */
export function useServeOrder() {
  const queryClient = useQueryClient();
  const branchId = useBranchId();
  const toast = useToast();
  return useMutation({
    retry: false,
    mutationFn: (vars: { orderId: string; doneMessage: string }) =>
      staffFloorApi.serveOrder(vars.orderId),
    onSuccess: (_result, vars) =>
      toast.show(vars.doneMessage, { tone: 'success', durationMs: 3000 }),
    onSettled: (_data, error) =>
      refreshStaff(queryClient, branchId, ['ready', 'floor', 'session'], error !== null),
  });
}

export function useOpenSession() {
  const queryClient = useQueryClient();
  const branchId = useBranchId();
  const toast = useToast();
  return useMutation({
    mutationFn: (vars: { input: OpenSessionInput; idempotencyKey: string }) =>
      staffFloorApi.openSession(vars.input, vars.idempotencyKey),
    onSuccess: (result) =>
      toast.show(`${result.location_label} opened.`, { tone: 'success', durationMs: 4000 }),
    onSettled: (_data, error) =>
      refreshStaff(queryClient, branchId, ['floor', 'sessions'], error !== null),
  });
}

export function useTransferSession() {
  const queryClient = useQueryClient();
  const branchId = useBranchId();
  const toast = useToast();
  return useMutation({
    mutationFn: (vars: { fromLocationId: string; input: TransferInput }) =>
      staffFloorApi.transfer(vars.fromLocationId, vars.input),
    onSuccess: (result) =>
      toast.show(`Moved to ${result.new_location_label}.`, { tone: 'success', durationMs: 4000 }),
    onSettled: (_data, error) =>
      refreshStaff(
        queryClient,
        branchId,
        ['floor', 'sessions', 'session', 'assistance'],
        error !== null,
      ),
  });
}
